import { Alert, Button, Stack, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useEffect, useState } from "react";

import AppDialog from "components/common/AppDialog";
import type {
  ScheduledRunInfo,
  UnsafeHoursWindow,
} from "features/adminUsers/adminUsers";
import { formatApiError } from "utils/formatApiError";

import {
  browserTimezone,
  formatScheduledRun,
  timeUntil,
} from "./scheduledRuns";
import { isInsideUnsafeHours } from "./unsafeHours";

/** The backend refuses anything closer than five minutes out. */
const MIN_LEAD_MINUTES = 5;
const MAX_HORIZON_DAYS = 30;

export interface ScheduleRunDialogProps {
  open: boolean;
  /** "Run all" for a job, "Run" for a single task - only the wording differs. */
  what: string;
  /** The booking already in place, when the dialog is opened to move it. */
  existing?: ScheduledRunInfo | null;
  saving: boolean;
  /** The unsafe hours the job is held to (blockedUnsafeHours): a moment inside
   *  them is warned about, not refused - the bot refuses the launch itself. */
  unsafeHours?: UnsafeHoursWindow | null;
  onClose: () => void;
  onSubmit: (isoInstant: string, timezone: string) => Promise<unknown>;
}

/** A domain refusal says {code, message}; a field one is a DRF map. */
function errorText(error: unknown): string {
  const body = (error as { data?: unknown } | undefined)?.data;
  if (body && typeof body === "object") {
    const message = (body as Record<string, unknown>).message;
    if (typeof message === "string" && message) return message;
  }
  return formatApiError(body, "Could not schedule the run.");
}

function roundedDefault(): Date {
  const start = new Date(Date.now() + 60 * 60 * 1000);
  start.setMinutes(0, 0, 0);
  return start;
}

export default function ScheduleRunDialog({
  open,
  what,
  existing,
  saving,
  unsafeHours,
  onClose,
  onSubmit,
}: ScheduleRunDialogProps) {
  const [value, setValue] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const zone = browserTimezone();

  useEffect(() => {
    if (!open) return;
    setError(null);
    setValue(existing ? new Date(existing.scheduled_at) : roundedDefault());
  }, [open, existing]);

  const minDateTime = new Date(Date.now() + MIN_LEAD_MINUTES * 60 * 1000);
  const maxDateTime = new Date(
    Date.now() + MAX_HORIZON_DAYS * 24 * 3600 * 1000,
  );
  const chosen =
    value != null && !Number.isNaN(value.getTime()) && value > minDateTime
      ? value.toISOString()
      : null;
  const insideUnsafeHours =
    chosen != null &&
    unsafeHours != null &&
    isInsideUnsafeHours(unsafeHours, new Date(chosen));

  const submit = async () => {
    if (chosen == null) return;
    setError(null);
    try {
      // toISOString carries the instant; the zone travels beside it so the
      // Slack reminder can say the time back in the operator's own clock.
      await onSubmit(chosen, zone);
      onClose();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={existing ? `Move the scheduled ${what}` : `Schedule ${what}`}
      maxWidth="xs"
      showCloseIcon
      actions={
        <>
          <Button onClick={onClose} disabled={saving}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => void submit()}
            disabled={saving || chosen == null}
          >
            {saving ? "Saving…" : existing ? "Move it" : "Schedule it"}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            label="Date and time"
            value={value}
            onChange={(next) => setValue(next)}
            minDateTime={minDateTime}
            maxDateTime={maxDateTime}
            ampm
            slotProps={{ textField: { fullWidth: true, size: "small" } }}
          />
        </LocalizationProvider>
        <Typography variant="caption" color="text.secondary">
          Your local time ({zone}).
          {chosen
            ? ` The run starts ${timeUntil(chosen)}, on ${formatScheduledRun(chosen)}.`
            : ` Pick a moment at least ${MIN_LEAD_MINUTES} minutes from now.`}
        </Typography>
        {insideUnsafeHours && unsafeHours && (
          <Alert severity="warning">
            This moment is inside the job's unsafe hours ({unsafeHours.label}),
            so the run will not start. Pick a time outside them, or turn off
            "Prevent running during unsafe hours" on the job.
          </Alert>
        )}
        <Alert severity="info">
          Slack gets a heads-up 30 minutes before the start, naming the job and
          who scheduled it. Until then - and until the run actually starts -
          this booking can be cancelled from the panel.
        </Alert>
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </AppDialog>
  );
}
